import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { AnnotationDetails } from '../../../../services/abcdeAPI';

interface ABCDEHeaderProps {
  annotationId: string;
  annotationDetails: AnnotationDetails | null;
  isUpdating: boolean;
  existingResultsCount: number;
  onBack: () => void;
  loadingDetails: boolean;
}

const ABCDEHeader: React.FC<ABCDEHeaderProps> = ({
  annotationId,
  annotationDetails,
  isUpdating,
  existingResultsCount,
  onBack,
  loadingDetails
}) => {
  const theme = useTheme();

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              ABCDE Analysis
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Melanoma Detection Assessment Protocol
              {isUpdating && (
                <Chip 
                  label="Updating Existing Assessment" 
                  color="warning" 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              )}
              {existingResultsCount > 0 && !isUpdating && (
                <Chip 
                  label={`${existingResultsCount} Previous Assessment(s)`}
                  color="info" 
                  size="small" 
                  sx={{ ml: 1 }} 
                />
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            Back
          </Button>
        </Box>
      </Box>
      
      {annotationDetails && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Annotation Details
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Chip label={`ID: ${annotationId}`} variant="outlined" size="small" />
            <Chip label={`Label: ${annotationDetails.label}`} variant="outlined" size="small" />
            <Chip label={`Position: ${Math.round(annotationDetails.x * 100)}%, ${Math.round(annotationDetails.y * 100)}%`} variant="outlined" size="small" />
            {annotationDetails.confidence && (
              <Chip label={`Confidence: ${Math.round(annotationDetails.confidence * 100)}%`} variant="outlined" size="small" />
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ABCDEHeader;
